import type { SearchResultResource } from "@/lib/api-types";

/**
 * The slug -> group-label lookup (Story 9.2, judgement call 3). `GET
 * /search` sends only the bare slug on each result (`app.domain.search`'s
 * own docstring: the frontend derives the plural group label itself) — this
 * is that lookup, once, covering exactly the four real slugs Story 9.1
 * registers today (`app.domain.property.PROPERTY_SLUG` etc.). Phase 1b added
 * `equipment` (Story 12.1); Story 16.2 registers the sixth and final
 * previously-reserved slot, `vendor` (`app.domain.vendors.registration.
 * VENDOR_SLUG`) — both additions were a one-line entry here, never touching
 * `SearchOverlay.tsx`'s own rendering logic.
 */
const SEARCH_GROUP_LABELS: Record<string, string> = {
  property: "Properties",
  unit: "Units",
  tenant: "Tenants",
  document: "Documents",
  equipment: "Equipment",
  vendor: "Vendors",
};

/** Falls back to the raw slug itself for anything not yet registered here
 * (a future entity type the frontend hasn't been taught a plural for yet)
 * rather than throwing — a missing label is a cosmetic gap, not a reason to
 * break search entirely. */
export function labelForSearchGroup(slug: string): string {
  return SEARCH_GROUP_LABELS[slug] ?? slug;
}

export interface SearchResultGroupItem {
  result: SearchResultResource;
  /** This result's position in the original flat `results` array — the
   * one Up/Down/Enter actually navigates (AC: "move between results", not
   * "between groups"). Grouping below is for visual rendering only and
   * never reorders results relative to each other, so `index` here and the
   * flat array stay in lockstep. */
  index: number;
}

export interface SearchResultGroup {
  slug: string;
  label: string;
  items: SearchResultGroupItem[];
}

/**
 * Groups an already-ordered flat result list by slug, for visual rendering
 * only (Story 9.2 Intent: "grouped only visually, traversed as one flat,
 * ordered list for Up/Down"). Groups appear in first-encountered order —
 * `app.domain.search.search`'s own per-entity loop already returns results
 * contiguous by slug (property, then unit, then tenant, then document), but
 * this does not assume that: a result of a slug already seen is appended to
 * its existing group wherever it falls in the flat list, rather than
 * requiring slugs to be contiguous.
 */
export function groupSearchResults(results: SearchResultResource[]): SearchResultGroup[] {
  const groups: SearchResultGroup[] = [];
  const groupIndexBySlug = new Map<string, number>();

  results.forEach((result, index) => {
    const existingGroupIndex = groupIndexBySlug.get(result.slug);
    if (existingGroupIndex === undefined) {
      groupIndexBySlug.set(result.slug, groups.length);
      groups.push({ slug: result.slug, label: labelForSearchGroup(result.slug), items: [{ result, index }] });
    } else {
      groups[existingGroupIndex]?.items.push({ result, index });
    }
  });

  return groups;
}
